/**
 * Inline contact create/edit modal on the project form.
 *
 * - Typing a new name in the contacts slim-select and clicking "+" opens the
 *   modal (prefilled with the typed text) instead of creating a "? <name>"
 *   stub contact.
 * - Clicking an already-selected contact chip opens the modal in edit mode
 *   instead of navigating to the contact page.
 *
 * Saves via /projects/api/contacts/save/, then inserts/updates the option in
 * the contacts select and refreshes the SlimSelect instance.
 */
import * as bootstrap from 'bootstrap';
import { initContactRecommendRating } from './contactRecommendRating.js';

function getCsrfToken() {
    const el = document.querySelector('[name=csrfmiddlewaretoken]');
    return el ? el.value : '';
}

function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
}

const FIELDS = ['is_company', 'first_name', 'last_name', 'company', 'position', 'email', 'phone', 'description'];

export function installContactModal() {
    const modalEl = document.getElementById('contactEditModal');
    const select = document.getElementById('id_contacts');
    if (!modalEl || !select) return;

    // Escape the .main-content stacking context (z-index trap)
    document.body.appendChild(modalEl);

    const field = (name) => modalEl.querySelector(`#contact-modal-${name}`);
    const titleEl = modalEl.querySelector('#contactEditModalLabel');
    const personFields = modalEl.querySelector('#contact-modal-person-fields');
    const companyLabel = modalEl.querySelector('#contact-modal-company-label');
    const ratingSection = modalEl.querySelector('#contact-modal-rating-section');
    const ratingContainer = modalEl.querySelector('#contact-modal-rating');
    const fullLink = modalEl.querySelector('#contact-modal-full-link');
    const errorBox = modalEl.querySelector('#contact-modal-error');
    const saveBtn = modalEl.querySelector('#contact-modal-save');

    let editingId = null;

    function togglePersonFields() {
        const isCompany = field('is_company').checked;
        personFields.style.display = isCompany ? 'none' : '';
        companyLabel.textContent = isCompany ? 'Company name' : 'Company';
    }
    field('is_company').addEventListener('change', togglePersonFields);

    function openModal(data = {}) {
        editingId = data.id || null;

        field('is_company').checked = !!data.is_company;
        FIELDS.filter(f => f !== 'is_company').forEach(f => {
            field(f).value = data[f] || '';
        });
        togglePersonFields();

        titleEl.textContent = editingId ? 'Edit contact' : 'Add new contact';
        errorBox.style.display = 'none';
        saveBtn.disabled = false;
        saveBtn.textContent = 'Save contact';

        if (editingId) {
            ratingSection.style.display = '';
            ratingContainer.dataset.contactId = editingId;
            ratingContainer.dataset.rating = data.recommend_likelihood ?? '';
            ratingContainer.dataset.setBy = data.recommend_set_by || '';
            ratingContainer.dataset.setAt = data.recommend_set_at || '';
            initContactRecommendRating(ratingContainer);
            fullLink.href = `/projects/contact/${editingId}/`;
            fullLink.style.display = '';
        } else {
            ratingSection.style.display = 'none';
            fullLink.style.display = 'none';
        }

        bootstrap.Modal.getOrCreateInstance(modalEl).show();
        setTimeout(() => {
            const first = data.is_company ? field('company') : field('first_name');
            first.focus();
        }, 150);
    }

    // --- New contact: hook the slim-select "+" (addable) ------------------
    if (select.slim) {
        select.slim.addable = (value) => {
            select.slim.search('');
            select.slim.close();
            openModal({ last_name: value });
            return false; // cancel slim-select's default raw-text option
        };
    }

    // --- Edit contact: click on a selected chip ---------------------------
    // Capture phase so we beat both the anchor navigation and slim-select's
    // container click (which would open the dropdown).
    document.addEventListener('click', async (e) => {
        const link = e.target.closest('.ss-value a[href*="/projects/contact/"]');
        if (!link) return;
        e.preventDefault();
        e.stopPropagation();
        const match = link.getAttribute('href').match(/\/contact\/(\d+)/);
        if (!match) return;
        try {
            const resp = await fetch(`/projects/api/contacts/${match[1]}/`);
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            openModal(await resp.json());
        } catch (err) {
            console.error('Failed to load contact:', err);
        }
    }, true);

    // --- Save --------------------------------------------------------------
    saveBtn.addEventListener('click', async () => {
        const payload = { id: editingId };
        payload.is_company = field('is_company').checked;
        FIELDS.filter(f => f !== 'is_company').forEach(f => {
            payload[f] = field(f).value.trim();
        });

        saveBtn.disabled = true;
        saveBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Saving…';
        errorBox.style.display = 'none';

        try {
            const resp = await fetch('/projects/api/contacts/save/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCsrfToken(),
                },
                body: JSON.stringify(payload),
            });
            const data = await resp.json();
            if (!resp.ok || !data.success) {
                errorBox.innerHTML = (data.errors || [data.error || 'Failed to save contact.']).map(escapeHtml).join('<br>');
                errorBox.style.display = 'block';
                return;
            }
            upsertContactOption(data.id, data.label, !editingId);
            bootstrap.Modal.getOrCreateInstance(modalEl).hide();
        } catch (err) {
            errorBox.textContent = 'Failed to save contact.';
            errorBox.style.display = 'block';
            console.error('Failed to save contact:', err);
        } finally {
            saveBtn.disabled = false;
            saveBtn.textContent = 'Save contact';
        }
    });

    function upsertContactOption(id, label, selectIt) {
        let opt = select.querySelector(`option[value="${id}"]`);
        if (!opt) {
            opt = document.createElement('option');
            opt.value = id;
            select.appendChild(opt);
        }
        // Same anchor markup as setupSlimSelect applies via the linkModel attr
        opt.innerHTML = `<a href="/projects/contact/${id}" target="_blank"> ${escapeHtml(label)} </a>`;
        if (selectIt) opt.selected = true;

        if (select.slim) {
            const opts = Array.from(select.options).map(o => ({
                text: o.textContent.trim(),
                innerHTML: o.innerHTML,
                value: o.value,
                selected: o.selected,
            }));
            select.slim.setData(opts);
        }

        // Newly selected contact must reach the form autosave
        if (selectIt) {
            select.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }
}
